import { Button, ConditionViewerItem, ConditionViewerTerm } from '@customerio/pluma-components/react';
import { useCampaignOptional } from '@/services/db/campaign/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { isEmptyString } from '@/utils/string';
import { ConditionType, peopleFilterBuilder } from '@/features/dryad';

type SegAttrRowProps = {
  /** The leaf node's id, supplied by the group to every registered view. */
  id: string;
};

/**
 * The attribute-trigger campaign's "who matches my trigger" condition, entered
 * only through the trigger panel's "matches" deep link. Nothing in the wire is
 * editable (see seg-attr-plugin), so the row is a read-only ConditionViewer
 * sentence that owns its whole surface: the group renders it bare
 * (ownsRowChrome) and the remove X lives in the viewer item's actions slot,
 * matching the legacy locked row — a label plus its delete X, nothing else.
 */
export function SegAttrRow({ id }: SegAttrRowProps) {
  const workspaceId = requireWorkspaceId();
  const meta = peopleFilterBuilder.useMeta();
  const { state } = peopleFilterBuilder.useLeafState(id, ConditionType.SegAttr);
  const { removeNode } = peopleFilterBuilder.useBuilderActions();

  const campaignId = state.campaignId;
  // A campaign this workspace can't resolve (deleted, or the fetch failed)
  // still renders — the label falls back to the raw id.
  const campaign = useCampaignOptional({
    workspaceId,
    id: campaignId != null ? String(campaignId) : '',
    enabled: campaignId != null,
  });

  const name = campaign.data?.name;
  let campaignLabel: string;
  if (name != null && !isEmptyString(name)) {
    campaignLabel = name;
  } else if (campaign.isLoading) {
    campaignLabel = 'Loading…';
  } else {
    campaignLabel = `Campaign ${campaignId}`;
  }

  return (
    <ConditionViewerItem
      data-testid="dryad-seg-attr-row"
      icon="trigger"
      actions={
        <Button
          data-testid="dryad-row-remove"
          aria-label="Remove condition"
          icon="close"
          isIconOnly
          variant="tertiary"
          size="sm"
          onClick={() => removeNode(id)}
          isDisabled={meta.isDisabled}
        />
      }
    >
      <ConditionViewerTerm>Trigger</ConditionViewerTerm> in <ConditionViewerTerm>{campaignLabel}</ConditionViewerTerm>
    </ConditionViewerItem>
  );
}

// The group renders a chrome-owning view bare: no shared remove button — the
// row supplies its own in the viewer item's actions slot (see
// viewOwnsRowChrome in people-filter-builder.tsx).
SegAttrRow.ownsRowChrome = true;
